import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NewWorkLog from "@/pages/NewWorkLog";
import { LogsList, LogDetail } from "@/pages/Logs";
import AdminExport from "@/pages/AdminExport";
import AdminUsers from "@/pages/AdminUsers";
import SuperStats from "@/pages/SuperStats";
import Fichar from "@/pages/Fichar";
import SadminTimeClock from "@/pages/SadminTimeClock";
import { PreJobChecksList, PreJobCheckDetail } from "@/pages/PreJobCheck";
import NewVehicleInspection, { VehicleInspectionsList } from "@/pages/VehicleInspection";
import Sugerencias, { AdminSugerencias } from "@/pages/Sugerencias";
import ChangePassword from "@/pages/ChangePassword";
import Orders from "@/pages/Orders";
import Crane from "@/pages/Crane";
import MyPjc from "@/pages/MyPjc";
import AdminPjc from "@/pages/AdminPjc";

function Protected() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-display font-black text-2xl tracking-tight">ETEL · Cargando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force first-login password change — block every route except /change-password itself
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

function AdminOnly() {
  const { user } = useAuth();
  if (user?.role !== "admin" && user?.role !== "sadmin") return <Navigate to="/" replace />;
  return <Outlet />;
}

function FicharOnly() {
  const { user } = useAuth();
  if (!user?.can_clock_in) return <Navigate to="/" replace />;
  return <Outlet />;
}

function SadminOnly() {
  const { user } = useAuth();
  if (user?.role !== "sadmin") return <Navigate to="/" replace />;
  return <Outlet />;
}

function App() {
  // Register service worker once, at app root, so PWA install + push are available to all users.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Protected />}>
              <Route path="/change-password" element={<ChangePassword />} />
              <Route element={<Layout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/new" element={<NewWorkLog />} />
                <Route path="/edit/:id" element={<NewWorkLog />} />
                <Route path="/logs" element={<LogsList />} />
                <Route path="/logs/:id" element={<LogDetail />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/crane" element={<Crane />} />
                <Route path="/pjc/mine" element={<MyPjc />} />
                <Route path="/admin/pjc" element={<AdminPjc />} />
                <Route element={<AdminOnly />}>
                  <Route path="/export" element={<AdminExport />} />
                </Route>
                <Route element={<SadminOnly />}>
                  <Route path="/users" element={<AdminUsers />} />
                  <Route path="/sadmin/stats" element={<SuperStats />} />
                  <Route path="/sadmin/fichajes" element={<SadminTimeClock />} />
                </Route>
                <Route element={<FicharOnly />}>
                  <Route path="/fichar" element={<Fichar />} />
                </Route>
                <Route path="/pre-job-checks" element={<PreJobChecksList />} />
                <Route path="/pre-job-checks/:id" element={<PreJobCheckDetail />} />
                <Route path="/vehicle-inspection/new" element={<NewVehicleInspection />} />
                <Route path="/vehicle-inspections" element={<VehicleInspectionsList />} />
                <Route path="/sugerencias" element={<Sugerencias />} />
                <Route path="/admin/sugerencias" element={<AdminSugerencias />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
